import type { LucideIcon } from 'lucide-react'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

export interface SideNavItemProps {
  icon: LucideIcon
  label: string
  /** How many things the entry holds; shown as a badge when above zero. */
  count?: number
  /** Colours the count as an exclusion rather than an inclusion. */
  negated?: boolean
  active?: boolean
  onSelect: () => void
  /** When given, an × on the right clears the entry. */
  onRemove?: () => void
}

/**
 * One entry of a vertical nav list: icon, label, how many things it holds, and
 * an × that clears it. Every entry is one line high, so the list does not
 * reflow as things are picked. The active entry is marked with a bar down its
 * left edge.
 */
export function SideNavItem({
  icon: Icon,
  label,
  count = 0,
  negated,
  active,
  onSelect,
  onRemove,
}: SideNavItemProps) {
  const { t } = useTranslation()

  return (
    <div
      className={cn(
        'border-l-2 border-transparent hover:bg-accent/50',
        active && 'border-primary bg-(--ad-primary-soft) hover:bg-(--ad-primary-soft)'
      )}
    >
      <div className="flex h-11 items-center gap-3 px-3">
        <button
          type="button"
          onClick={onSelect}
          className="flex h-full min-w-0 flex-1 items-center gap-3 text-left"
        >
          <Icon className={cn('size-5 shrink-0 text-muted-foreground', active && 'text-primary')} />
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-sm font-semibold',
              active && 'text-primary'
            )}
          >
            {label}
          </span>
          {count > 0 && (
            <Badge variant={negated ? 'warning' : 'blue'} className="shrink-0 tabular-nums">
              {count}
            </Badge>
          )}
        </button>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={t('common.remove')}
            className="shrink-0 text-muted-foreground hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        )}
      </div>
    </div>
  )
}
